import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Clock, Send, Loader2, Calendar as CalendarIcon } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { SEO } from "../components/SEO";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { CONTACT_INFO, SERVICES, MAPS_LINK, MAPS_EMBED_URL } from "../data/siteData";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const spring = { type: "spring", stiffness: 200, damping: 20 };

const initialForm = { parent_name: "", email: "", phone: "", child_name: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [dob, setDob] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dob) {
      toast.error("Please select your child's date of birth.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, child_dob: dob ? format(dob, "yyyy-MM-dd") : null });
      trackEvent("contact_form_submit", { service: form.service || "unspecified" });
      toast.success("Message sent! We'll be in touch within one business day.");
      setForm(initialForm);
      setDob(null);
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Thrive Pediatric Therapy | Nassau, Bahamas"
        description="Get in touch with Thrive Pediatric Therapy in Nassau, Bahamas. Message us, chat on WhatsApp or book an evaluation online through Jane."
      />
      <PageHeader
        testId="contact-header"
        eyebrow="Contact Us"
        title="We can't wait to help your child thrive!"
        description="Questions about your child's development? Ready to book? Send us a message and we'll get back to you ASAP!"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid lg:grid-cols-5 gap-10">
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={spring}
            data-testid="contact-form"
            className="lg:col-span-3 bg-white rounded-[2rem] border border-border p-8 sm:p-10 shadow-sm space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="parent_name" className="font-extrabold text-navy">Parent / Guardian Name *</Label>
                <Input
                  id="parent_name"
                  data-testid="contact-name-input"
                  required
                  value={form.parent_name}
                  onChange={(e) => update("parent_name", e.target.value)}
                  placeholder="Your name"
                  className="rounded-2xl border-border h-12 focus-visible:ring-teal"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="font-extrabold text-navy">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email-input"
                  required
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@example.com"
                  className="rounded-2xl border-border h-12 focus-visible:ring-teal"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="font-extrabold text-navy">Phone *</Label>
                <Input
                  id="phone"
                  data-testid="contact-phone-input"
                  required
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+1 (242) 000-0000"
                  className="rounded-2xl border-border h-12 focus-visible:ring-teal"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="child_name" className="font-extrabold text-navy">Child's Name *</Label>
                <Input
                  id="child_name"
                  data-testid="contact-child-name-input"
                  required
                  value={form.child_name}
                  onChange={(e) => update("child_name", e.target.value)}
                  placeholder="Your child's first name"
                  className="rounded-2xl border-border h-12 focus-visible:ring-teal"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-extrabold text-navy">Child's Date of Birth *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="contact-dob-picker"
                      className="w-full h-12 rounded-2xl border border-border bg-white px-4 flex items-center gap-2.5 text-sm font-bold text-left transition-colors hover:border-teal focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
                    >
                      <CalendarIcon className="w-4 h-4 text-teal-dark shrink-0" />
                      <span className={dob ? "text-navy" : "text-navy-light/60"}>
                        {dob ? format(dob, "MMMM d, yyyy") : "Select date"}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 rounded-2xl" align="start">
                    <Calendar
                      mode="single"
                      selected={dob}
                      onSelect={setDob}
                      captionLayout="dropdown"
                      fromYear={2007}
                      toYear={new Date().getFullYear()}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-extrabold text-navy">Service you're curious about *</Label>
              <Select value={form.service} onValueChange={(v) => update("service", v)} required>
                <SelectTrigger data-testid="contact-service-select" className="rounded-2xl border-border h-12 focus:ring-teal">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.slug} value={s.fullName} data-testid={`contact-service-option-${s.slug}`}>
                      {s.fullName}
                    </SelectItem>
                  ))}
                  <SelectItem value="Not sure yet" data-testid="contact-service-option-unsure">Not sure yet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="font-extrabold text-navy">How can we help? *</Label>
              <Textarea
                id="message"
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Tell us a little about your child and what brings you to Thrive..."
                className="rounded-2xl border-border focus-visible:ring-teal resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="inline-flex items-center justify-center gap-2 bg-teal-dark text-white font-extrabold px-8 py-4 rounded-full shadow-lg shadow-teal-dark/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1C565E] disabled:opacity-60 disabled:translate-y-0 focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 w-full sm:w-auto"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" strokeWidth={2.5} />}
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.12 }}
            className="lg:col-span-2 space-y-6"
          >
            <div data-testid="contact-info-card" className="bg-navy text-white rounded-[2rem] p-8">
              <h2 className="font-display text-xl font-semibold">Visit our clinic</h2>
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-sunny shrink-0" />
                  <a
                    href={MAPS_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-location"
                    className="text-white/85 hover:text-white underline decoration-white/30 underline-offset-4 transition-colors"
                  >
                    {CONTACT_INFO.location}
                  </a>
                </li>
                <li className="flex items-start gap-3"><Mail className="w-5 h-5 text-sunny shrink-0" /><span data-testid="contact-email" className="text-white/85">{CONTACT_INFO.email}</span></li>
                <li className="flex items-start gap-3"><Phone className="w-5 h-5 text-sunny shrink-0" /><span data-testid="contact-phone" className="text-white/85">{CONTACT_INFO.phone}</span></li>
                <li className="flex items-start gap-3"><Clock className="w-5 h-5 text-sunny shrink-0" /><span data-testid="contact-hours" className="text-white/85">{CONTACT_INFO.hours}</span></li>
              </ul>
            </div>

            <div data-testid="contact-map" className="rounded-[2rem] overflow-hidden border border-border shadow-sm">
              <iframe
                title="Thrive Pediatric Therapy location map - 11 Mount Royal Ave, Nassau"
                src={MAPS_EMBED_URL}
                className="w-full h-64 sm:h-72 border-0"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.aside>
        </div>
      </section>
    </div>
  );
}
